import React from 'react';
import { Box, Typography } from '@mui/material';
import { ChevronRight } from '@mui/icons-material';
import './Pagination.css';

export interface PaginationProps {
  currentPage: number;
  totalPages: number;
  totalItems: number;
  itemsPerPage: number;
  onPageChange: (page: number) => void;
  itemLabel?: string; // e.g., 'users', 'items', 'products', 'services', 'profiles'
  // Legacy props for backward compatibility (ignored in new design)
  showItemCount?: boolean;
  showPrevNext?: boolean;
  disabled?: boolean;
}

export interface NewPaginationProps {
  currentPage: number;
  totalPages: number;
  hasNext: boolean;
  onPageChange: (page: number) => void;
  itemsMessage: string | null;
}

const Pagination: React.FC<PaginationProps> = ({
  currentPage,
  totalPages,
  totalItems,
  itemsPerPage,
  onPageChange,
  itemLabel = 'items',
  disabled = false,
  // showItemCount and showPrevNext are ignored - always show item count and Next button
}) => {
  const startItem = (currentPage - 1) * itemsPerPage + 1;
  const endItem = Math.min(currentPage * itemsPerPage, totalItems);

  const renderPageNumbers = () => {
    const pages: (number | string)[] = [];
    
    // Always show first page
    pages.push(1);
    
    if (currentPage > 3) {
      pages.push('...');
    }
    
    // Show current page and adjacent pages
    for (let i = Math.max(2, currentPage - 1); i <= Math.min(totalPages - 1, currentPage + 1); i++) {
      if (i !== 1 && i !== totalPages) {
        pages.push(i);
      }
    }
    
    if (currentPage < totalPages - 2) {
      pages.push('...');
    }
    
    // Always show last page if more than 1 page
    if (totalPages > 1) {
      pages.push(totalPages);
    }
    
    return pages;
  };

  const handlePageClick = (page: number) => {
    if (!disabled) {
      onPageChange(page);
    }
  };

  const handleNextClick = () => {
    if (!disabled && currentPage < totalPages) {
      onPageChange(currentPage + 1);
    }
  };

  return (
    <Box className="pagination-container">
      <Box className="pagination-controls">
        {renderPageNumbers().map((page, index) => (
          <Box
            key={index}
            onClick={() => typeof page === 'number' && handlePageClick(page)}
            className={`pagination-page-button ${currentPage === page ? 'active' : ''} ${typeof page !== 'number' ? 'ellipsis' : ''} ${disabled ? 'disabled' : ''}`}
          >
            <Typography className="pagination-page-text">
              {page}
            </Typography>
          </Box>
        ))}
        
        {currentPage < totalPages && (
          <Box
            onClick={handleNextClick}
            className={`pagination-next-button ${disabled ? 'disabled' : ''}`}
          >
            <Typography className="pagination-next-text">
              Next
            </Typography>
            <ChevronRight className="pagination-next-icon" />
          </Box>
        )}
      </Box>
      
      <Typography className="pagination-item-count">
        {startItem}–{endItem} of {totalItems} {itemLabel}
      </Typography>
    </Box>
  );
};

const NewPagination: React.FC<NewPaginationProps> = ({
  currentPage,
  totalPages,
  hasNext,
  onPageChange,
  itemsMessage,
}) => {
  const renderPageNumbers = () => {
    const pages: (number | string)[] = [];
    
    // Only show pages we can actually navigate to
    for (let i = 1; i <= totalPages; i++) {
      // Show first page, last known page, current page, and adjacent pages
      if (i === 1 || i === totalPages || Math.abs(i - currentPage) <= 1) {
        pages.push(i);
      } else if (pages[pages.length - 1] !== '...') {
        pages.push('...');
      }
    }
    
    return pages;
  };

  const handlePageClick = (page: number) => {
    if (page <= totalPages) {
      onPageChange(page);
    }
  };

  const handleNextClick = () => {
    if (hasNext) {
      onPageChange(currentPage + 1);
    }
  };

  return (
    <Box className="pagination-container">
      <Box className="pagination-controls">
        {renderPageNumbers().map((page, index) => (
          <Box
            key={index}
            onClick={() => typeof page === 'number' && handlePageClick(page)}
            className={`pagination-page-button ${currentPage === page ? 'active' : ''} ${typeof page !== 'number' ? 'ellipsis' : ''}`}
          >
            <Typography className="pagination-page-text">
              {page}
            </Typography>
          </Box>
        ))}

        {hasNext && (
          <Box
            onClick={handleNextClick}
            className={`pagination-next-button`}
          >
            <Typography className="pagination-next-text">
              Next
            </Typography>
            <ChevronRight className="pagination-next-icon" />
          </Box>
        )}
      </Box>
      
      {itemsMessage !== null ? (
        <Typography className="pagination-item-count">
          {itemsMessage}
        </Typography>
      ) : undefined}
    </Box>
  );
};


export default Pagination;
export { NewPagination }
